import { z } from "zod";
/**
 * Reopening a book after approval (owner decision F4, 2026-11-02): an approval can be reopened to
 * make changes until the hardcover is ordered, and the family approves again before printing. Once
 * a print order is placed the book is locked, and a reopen is refused.
 *
 * - `MEMOIR_PLAN`: a guided memoir's approved chapter plan.
 * - `PRINT_APPROVAL`: a book's approved print files (the approved print package and the book's
 *   print approval beneath it). A picture book's print approval stays with its edition review.
 */
export const bookReopenTargetSchema = z.enum(["MEMOIR_PLAN", "PRINT_APPROVAL"]);
export const reopenBookCommandV1Schema = z
    .object({
    version: z.literal(1),
    target: bookReopenTargetSchema,
})
    .strict();
export const bookReopenResultV1Schema = z
    .object({
    version: z.literal(1),
    bookId: z.string().min(1).max(80),
    target: bookReopenTargetSchema,
    reopenedAt: z.string().datetime(),
})
    .strict();
/** The refusals a reopen can give, each with customer wording from the server. */
export const bookReopenRefusalCodes = [
    /** A print order is placed: the book is locked. */
    "BOOK_REOPEN_ORDERED",
    /** There is no approval to reopen (never given, or already reopened elsewhere). */
    "BOOK_REOPEN_NOT_APPROVED",
    /** This book's approval can't be reopened by the customer; support helps instead. */
    "BOOK_REOPEN_UNAVAILABLE",
];
/**
 * Whether this reader can reopen this approval now (`GET /books/{bookId}/reopen?target=`), so a
 * page shows the reopen, the hardcover lock, or nothing (P2-11, 2026-11-02). It reads the same
 * records the reopen command checks; the command still decides.
 *
 * - `REOPENABLE`: approved, and nothing is ordered.
 * - `ORDERED`: a print order is placed (or the book is being printed): the book is locked.
 * - `NOT_APPROVED`: there is no approval to reopen (never given, or already reopened).
 * - `UNAVAILABLE`: this book's approval isn't one the family can reopen; support helps.
 * - `NOT_ALLOWED`: this reader doesn't approve the book (a contributor or viewer).
 */
export const bookReopenStatusV1Schema = z
    .object({
    version: z.literal(1),
    bookId: z.string().min(1).max(80),
    target: bookReopenTargetSchema,
    state: z.enum(["REOPENABLE", "ORDERED", "NOT_APPROVED", "UNAVAILABLE", "NOT_ALLOWED"]),
})
    .strict();
//# sourceMappingURL=book-reopen.js.map